/**
 * i18next, configured once for the whole platform.
 *
 * The catalogues are bundled, so an instance is usable the moment it is created — the kiosk has to
 * render its lock screen with no network (spec/12 §1) and cannot wait for a fetch. A surface that
 * wants lazily loaded catalogues adds a backend to the instance it gets back.
 */
import i18next, {} from 'i18next';
import { defaultNamespace, namespaces, resources } from './catalogues.js';
import { fallbackLocale, locales } from './types.js';
const mergeResources = (extra) => {
    const merged = {};
    for (const [language, byNamespace] of Object.entries(resources)) {
        merged[language] = { ...byNamespace };
    }
    for (const [language, byNamespace] of Object.entries(extra ?? {})) {
        merged[language] = { ...merged[language], ...byNamespace };
    }
    return merged;
};
/**
 * A fresh, isolated i18next instance. Never the i18next singleton: the console can run a staff
 * language and a player-facing panel side by side, and tests must not leak state into each other.
 */
export function createI18n(options = {}) {
    const instance = i18next.createInstance();
    for (const plugin of options.use ?? [])
        instance.use(plugin);
    void instance.init({
        lng: options.language ?? fallbackLocale,
        fallbackLng: fallbackLocale,
        supportedLngs: [...locales],
        ns: [...namespaces],
        defaultNS: defaultNamespace,
        // Everything is bundled, so initialisation completes synchronously and `t` works on the next
        // line — the kiosk renders its lock screen before it has a network (spec/12 §1).
        resources: mergeResources(options.extraResources),
        interpolation: { escapeValue: options.escapeValue ?? false },
        returnNull: false,
        // A genuinely missing key renders as itself rather than blank — a safety net, not a fallback path.
        parseMissingKeyHandler: (key) => key,
    });
    return instance;
}
/**
 * Which of the shipped locales a preferred language maps to. With English the only locale, every
 * Accept-Language value resolves to `en`; the function stays so callers can keep asking.
 */
export function resolveLocale(preferred) {
    const base = preferred?.toLowerCase().split('-')[0] ?? '';
    return locales.includes(base) ? base : fallbackLocale;
}
//# sourceMappingURL=i18n.js.map